import { Box, Chip, Stack, Typography } from "@mui/material";
import { CLASSIFICATION_OPTIONS } from "./resourceUtils";
import { TableHeaderRow, TableShell, TableCell } from "../ui/TableShell";
import { getCommodityMeta } from "./functions";
import { getUnitFromMetric, resolveResourceUnit } from "../../utils/resourceFormatting";

const defaultClassificationOptions = CLASSIFICATION_OPTIONS;

const formatSummaryValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return { text: "—", isZero: true };
  }
  return { text: formatSummaryValue(value), isZero: false };
};

export const ResourceRollupTable = ({
  rows = [],
  emptyLabel,
  classificationOptions = defaultClassificationOptions,
  digits = 2,
}) => {
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  const gridTemplateColumns = {
    xs: "minmax(140px, 1.4fr) repeat(5, minmax(70px, 1fr))",
    sm: "1.8fr repeat(5, 1fr)",
  };
  return (
    <TableShell sx={{ overflowX: { xs: "auto", sm: "hidden" }, WebkitOverflowScrolling: "touch" }}>
      <TableHeaderRow
        gridTemplateColumns={gridTemplateColumns}
        gap={{ xs: 0.5, sm: 1 }}
        px={{ xs: 0.5, sm: 1 }}
        py={{ xs: 0.25, sm: 0.5 }}
      >
        <TableCell sx={{ borderRight: "1px solid rgba(0,0,0,0.08)", pr: 1 }} label="Commodity" />
        {classificationOptions.map((opt) => (
          <TableCell key={opt.value} align="center" label={opt.label} />
        ))}
      </TableHeaderRow>
      <Stack spacing={0} sx={{ px: { xs: 0.5, sm: 1 } }}>
        {rows.map((row) => {
          const unit = row.unit || getUnitFromMetric(row.metric);
          const meta = getCommodityMeta(row.commodity);
          const commodityLabel = meta?.label || row.commodity;
          const chipLabel = meta?.symbol || row.commodity;
          return (
            <Box
              key={`${row.commodity}-${row.metric || unit}`}
              sx={{
                display: "grid",
                gridTemplateColumns,
                gap: { xs: 0.5, sm: 1 },
                alignItems: "center",
              }}
            >
              <Box sx={{ borderRight: "1px solid rgba(0,0,0,0.08)", pr: 1, py: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Chip
                  label={chipLabel}
                  size="small"
                  sx={{
                    backgroundColor: meta?.color || "action.hover",
                    color: "#0f172a",
                    fontWeight: 700,
                    height: 24,
                  }}
                  title={commodityLabel}
                />
                <Typography variant="caption" fontWeight={600}>
                  {`${commodityLabel} (${unit})`}
                </Typography>
              </Stack>
            </Box>
              {classificationOptions.map((opt) => {
                const cell = formatCellValue(row[opt.value], digits);
                return (
                  <Typography
                    key={`${row.commodity}-${row.metric || unit}-${opt.value}`}
                    variant="caption"
                    color={cell.isZero ? "text.disabled" : "text.secondary"}
                    fontWeight={cell.isZero ? 400 : 700}
                    sx={{textAlign:"center"}}
                  >
                    {cell.text}
                  </Typography>
                );
              })}
            </Box>
          );
        })}
      </Stack>
    </TableShell>
  );
};

export const ResourceTable = ({
  values = [],
  classificationOptions = defaultClassificationOptions,
  digits = 2,
  showCommodityChip = false,
}) => {
  if (!values.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        No commodity values yet.
      </Typography>
    );
  }

  const commodityOrder = [];
  const commodityMetaByKey = new Map();
  const valuesByClassification = values.reduce((acc, val) => {
    const commodityKey = val.commodity?.id || val.commodity || val.commodity?.name || "Commodity";
    if (!commodityMetaByKey.has(commodityKey)) {
      const commodityName = val.commodity?.name || val.commodity || "Commodity";
      commodityMetaByKey.set(commodityKey, {
        key: commodityKey,
        name: commodityName,
        gradeUnit: resolveResourceUnit(
          val.grade_unit || val.commodity?.grade_unit,
          commodityName,
          "gradeUnit"
        ),
        containedUnit: resolveResourceUnit(
          val.contained_unit || val.commodity?.contained_unit,
          commodityName,
          "containedUnit"
        ),
      });
      commodityOrder.push(commodityKey);
    }
    if (!acc[val.classification_level]) {
      acc[val.classification_level] = {
        tonnage: val.tonnage_mt,
        grades: {},
        contained: {},
      };
    }
    acc[val.classification_level].grades[commodityKey] = val.grade_value;
    acc[val.classification_level].contained[commodityKey] = val.contained_value;
    if (
      acc[val.classification_level].tonnage === undefined ||
      acc[val.classification_level].tonnage === null ||
      acc[val.classification_level].tonnage === ""
    ) {
      acc[val.classification_level].tonnage = val.tonnage_mt;
    }
    return acc;
  }, {});

  const commodities = commodityOrder.map((key) => commodityMetaByKey.get(key));
  const hasMeaningfulValue = (value) => {
    if (value === null || value === undefined) return false;
    const normalized = `${value}`.trim();
    if (!normalized) return false;
    const numeric = Number(normalized);
    if (!Number.isNaN(numeric)) {
      return numeric !== 0;
    }
    return true;
  };
  const visibleClassificationRows = classificationOptions.filter((opt) => {
    const row = valuesByClassification[opt.value];
    if (!row) return false;
    if (hasMeaningfulValue(row.tonnage)) return true;
    if (commodities.some((commodity) => hasMeaningfulValue(row.grades?.[commodity.key]))) {
      return true;
    }
    if (commodities.some((commodity) => hasMeaningfulValue(row.contained?.[commodity.key]))) {
      return true;
    }
    return false;
  });

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse"
        }}
      >
        <Box component="thead">
          <Box component="tr" sx={{ backgroundColor: "rgba(34, 35, 37, 0.04)" }}>
            <Box component="th" rowSpan={2} sx={{ px: 1, py: 0.5, textAlign: "left" }}>
              <Typography variant="caption" fontWeight={700}>
                Classification
              </Typography>
            </Box>
            <Box component="th" rowSpan={2} sx={{ px: 1, py: 0.5, textAlign: "center" }}>
              <Typography variant="caption" fontWeight={700}>
                Tonnage (Mt)
              </Typography>
            </Box>
            <Box
              component="th"
              colSpan={commodities.length}
              sx={{ px: 1, py: 0.5, textAlign: "center" }}
            >
              <Typography variant="caption" fontWeight={700}>
                Grade
              </Typography>
            </Box>
            <Box
              component="th"
              colSpan={commodities.length}
              sx={{ px: 1, py: 0.5, textAlign: "center" }}
            >
              <Typography variant="caption" fontWeight={700}>
                Contained Metal
              </Typography>
            </Box>
          </Box>
          <Box component="tr" sx={{ backgroundColor: "rgba(34, 35, 37, 0.04)" }}>
            {commodities.map((commodity, commodityIndex) => {
              const meta = getCommodityMeta(commodity?.name || commodity?.key);
              const label = meta?.label || commodity?.name || "Commodity";
              return (
                <Box
                  component="th"
                  key={`grade-${commodity.key}`}
                  sx={{
                    px: 0,
                    pb: 0.5,
                    textAlign: "center",
                    pr: commodityIndex === commodities.length - 1 ? 2 : 0,
                  }}
                >
                  {showCommodityChip ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Chip
                        label={meta?.symbol || label}
                        size="small"
                        sx={{
                          backgroundColor: meta?.color || "action.hover",
                          color: "#0f172a",
                          fontWeight: 700,
                          height: 20,
                        }}
                      />
                      <Typography variant="caption" fontWeight={700}>
                        {commodity.gradeUnit}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption" fontWeight={700}>
                      {label} ({commodity.gradeUnit})
                    </Typography>
                  )}
                </Box>
              );
            })}
            {commodities.map((commodity, commodityIndex) => {
              const meta = getCommodityMeta(commodity?.name || commodity?.key);
              const label = meta?.label || commodity?.name || "Commodity";
              return (
                <Box
                  component="th"
                  key={`contained-${commodity.key}`}
                  sx={{
                    px: 0,
                    pb: 0.5,
                    textAlign: "center",
                    pl: commodityIndex === 0 ? 2 : 0,
                  }}
                >
                  {showCommodityChip ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Chip
                        label={meta?.symbol || label}
                        size="small"
                        sx={{
                          backgroundColor: meta?.color || "action.hover",
                          color: "#0f172a",
                          fontWeight: 700,
                          height: 20,
                        }}
                      />
                      <Typography variant="caption" fontWeight={700}>
                        {commodity.containedUnit}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption" fontWeight={700}>
                      {label} ({commodity.containedUnit})
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box component="tbody">
          {visibleClassificationRows.map((opt) => {
            const row = valuesByClassification[opt.value] || { grades: {}, contained: {} };
            const tonnage = formatCellValue(row.tonnage, digits);
            return (
              <Box
                component="tr"
                key={opt.value}
                sx={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}
              >
                <Box component="td" sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {opt.label}
                  </Typography>
                </Box>
                <Box component="td" sx={{ px: 1, py: 0.5, textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    color={tonnage.isZero ? "text.disabled" : "text.secondary"}
                    fontWeight={tonnage.isZero ? 400 : 700}
                  >
                    {tonnage.text}
                  </Typography>
                </Box>
                {commodities.map((commodity, commodityIndex) => {
                  const grade = formatCellValue(row.grades?.[commodity.key], digits);
                  return (
                    <Box
                      component="td"
                      key={`grade-${commodity.key}-${opt.value}`}
                      sx={{
                        px: 0,
                        py: 0.5,
                        textAlign: "center",
                        pr: commodityIndex === commodities.length - 1 ? 2 : 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color={grade.isZero ? "text.disabled" : "text.secondary"}
                        fontWeight={grade.isZero ? 400 : 700}
                      >
                        {grade.text}
                      </Typography>
                    </Box>
                  );
                })}
                {commodities.map((commodity, commodityIndex) => {
                  const contained = formatCellValue(row.contained?.[commodity.key], digits);
                  return (
                    <Box
                      component="td"
                      key={`contained-${commodity.key}-${opt.value}`}
                      sx={{
                        px: 0,
                        py: 0.5,
                        textAlign: "center",
                        pl: commodityIndex === 0 ? 2 : 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color={contained.isZero ? "text.disabled" : "text.secondary"}
                        fontWeight={contained.isZero ? 400 : 700}
                      >
                        {contained.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};



